/**
 * Opportunities on a customer (UX decision §5).
 *
 * An opportunity is a customer's potential or active piece of work. It moves
 * Lead → Qualified → Quoted → Scheduled → In progress → Invoiced → Paid (or
 * Lost) and produces its documents on the way: a Quote, the Job the approved
 * quote becomes, and the Invoice the job produces.
 *
 * Most opportunities are a `Deal` with documents linked to it (quote.dealId,
 * job.quoteId, invoice.jobId/quoteId/dealId). Documents created before deals
 * existed — or straight from the quote builder — have no deal, so a quote →
 * job → invoice chain without one is treated as an opportunity too. Either
 * way the stage is derived from the documents, never stored twice.
 */
import type { ApiDeal, ApiQuote, ApiJob, ApiInvoice } from './apiTypes'
import { zipWorkItems } from './workItem'

export type OpportunityStage =
  | 'LEAD'
  | 'QUALIFIED'
  | 'QUOTED'
  | 'SCHEDULED'
  | 'IN_PROGRESS'
  | 'INVOICED'
  | 'PAID'
  | 'LOST'

/** The pipeline in order (Lost sits outside it). */
export const OPPORTUNITY_STAGES: Exclude<OpportunityStage, 'LOST'>[] = [
  'LEAD',
  'QUALIFIED',
  'QUOTED',
  'SCHEDULED',
  'IN_PROGRESS',
  'INVOICED',
  'PAID',
]

export const OPPORTUNITY_STAGE_LABELS: Record<OpportunityStage, string> = {
  LEAD: 'Lead',
  QUALIFIED: 'Qualified',
  QUOTED: 'Quoted',
  SCHEDULED: 'Scheduled',
  IN_PROGRESS: 'In progress',
  INVOICED: 'Invoiced',
  PAID: 'Paid',
  LOST: 'Lost',
}

/** Details that are the record's own status, not a note on its stage. */
const OWN_STATUS = new Set(['Approved', 'Completed'])

/**
 * The stage chip's words: "Quoted · Not sent yet", but a finished job reads
 * "Completed" and an approved quote "Approved", as on the Work board, in
 * search and in notifications (UX-183).
 */
export function opportunityStatusLabel(o: Pick<Opportunity, 'stage' | 'detail'>): string {
  if (o.detail && OWN_STATUS.has(o.detail)) return o.detail
  return [OPPORTUNITY_STAGE_LABELS[o.stage], o.detail].filter(Boolean).join(' · ')
}

/** The one thing to do next, which creates or opens the stage's document. */
export type OpportunityNextStep =
  | { kind: 'createQuote' }
  | { kind: 'openQuote'; quoteId: string }
  | { kind: 'createJob'; quoteId: string }
  | { kind: 'openJob'; jobId: string }
  | { kind: 'createInvoice'; jobId: string; quoteId?: string }
  | { kind: 'openInvoice'; invoiceId: string }

export interface Opportunity {
  /** `deal:<id>` or the chain's lead document (`q:`/`job:`/`inv:`) — also its Work record id. */
  id: string
  deal?: ApiDeal
  quote?: ApiQuote
  job?: ApiJob
  invoice?: ApiInvoice
  name: string
  value: number
  stage: OpportunityStage
  /** Extra state inside the stage, e.g. "Not sent yet", or the own status "Approved" / "Completed". */
  detail?: string
  next: OpportunityNextStep | null
  closed: boolean
  updatedAt: string
}

const DEAD_QUOTE = new Set(['DECLINED', 'EXPIRED'])
const DEAD_INVOICE = new Set(['CANCELLED', 'VOIDED', 'WRITTEN_OFF'])

const newest = <T extends { updatedAt: string }>(xs: T[]): T | undefined =>
  [...xs].sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime())[0]

/** Work record id the Work page understands for this opportunity's most advanced document. */
export function opportunityRecordId(o: Pick<Opportunity, 'deal' | 'quote' | 'job' | 'invoice'>): string {
  if (o.invoice) return `inv:${o.invoice.id}`
  if (o.job) return `job:${o.job.id}`
  if (o.quote) return `q:${o.quote.id}`
  return `deal:${o.deal?.id ?? ''}`
}

interface Docs {
  quotes: ApiQuote[]
  jobs: ApiJob[]
  invoices: ApiInvoice[]
  /**
   * The viewer's role can't see quotes or invoices (Crew: the server strips
   * them). Their absence then says nothing about the work, so the deal's own
   * stage is used instead of "No quote yet".
   */
  docsHidden?: boolean
}

/** Stage, detail and next step from a deal (optional) and its documents. */
export function deriveOpportunity(deal: ApiDeal | undefined, docs: Docs): Pick<Opportunity, 'stage' | 'detail' | 'next' | 'quote' | 'job' | 'invoice'> {
  const liveQuotes = docs.quotes.filter((q) => !DEAD_QUOTE.has(q.status))
  const quote = newest(liveQuotes) ?? newest(docs.quotes)
  const job = newest(docs.jobs.filter((j) => j.status !== 'CANCELLED'))
  const liveInvoices = docs.invoices.filter((i) => !DEAD_INVOICE.has(i.status))
  const invoice = newest(liveInvoices.filter((i) => i.status !== 'PAID')) ?? newest(liveInvoices)

  if (deal?.stage === 'LOST') return { stage: 'LOST', next: null, quote, job, invoice }

  // Crew: the documents are hidden, so the server derived the real stage
  // name (backend lib/crewStages.ts). Show it, with no document next step.
  if (docs.docsHidden && deal?.pipelineStage) {
    return { stage: deal.pipelineStage, detail: deal.pipelineDetail ?? undefined, next: null, job }
  }
  if (docs.docsHidden && !deal && job?.billingStage) {
    return { stage: job.billingStage, next: { kind: 'openJob', jobId: job.id }, job }
  }

  if (liveInvoices.length > 0 && invoice) {
    const allPaid = liveInvoices.every((i) => i.status === 'PAID')
    if (allPaid) return { stage: 'PAID', next: null, quote, job, invoice }
    return {
      stage: 'INVOICED',
      detail: invoice.status === 'DRAFT' ? 'Not sent yet' : invoice.status === 'OVERDUE' ? 'Overdue' : undefined,
      next: { kind: 'openInvoice', invoiceId: invoice.id },
      quote,
      job,
      invoice,
    }
  }

  if (job) {
    if (job.status === 'COMPLETED') {
      return {
        stage: 'IN_PROGRESS',
        detail: 'Completed',
        next: { kind: 'createInvoice', jobId: job.id, quoteId: job.quoteId ?? quote?.id },
        quote,
        job,
      }
    }
    return {
      stage: job.status === 'IN_PROGRESS' ? 'IN_PROGRESS' : 'SCHEDULED',
      next: { kind: 'openJob', jobId: job.id },
      quote,
      job,
    }
  }

  if (liveQuotes.length > 0 && quote) {
    if (quote.status === 'ACCEPTED') {
      return { stage: 'QUOTED', detail: 'Approved', next: { kind: 'createJob', quoteId: quote.id }, quote }
    }
    return {
      stage: 'QUOTED',
      detail: quote.status === 'DRAFT' ? 'Not sent yet' : undefined,
      next: { kind: 'openQuote', quoteId: quote.id },
      quote,
    }
  }

  // No live documents. Without a deal, a chain whose quotes were all declined
  // or expired is a lost opportunity.
  if (!deal) return { stage: 'LOST', detail: quote ? 'Quote declined' : undefined, next: null, quote }

  if (deal.stage === 'LEAD') return { stage: 'LEAD', next: { kind: 'createQuote' }, quote }
  // Quotes hidden from this role: say what the deal says, nothing more. A
  // Qualified deal stays Qualified (below); later deal stages had a quote.
  if (docs.docsHidden && deal.stage !== 'QUALIFIED') return { stage: 'QUOTED', detail: deal.stage === 'WON' ? 'Approved' : undefined, next: null }
  // Won with no live quote: the quote is what's missing.
  if (deal.stage === 'WON') return { stage: 'QUOTED', detail: 'No quote yet', next: { kind: 'createQuote' }, quote }
  // QUALIFIED, and PROPOSAL / NEGOTIATION with no live quote, are still being
  // qualified. The Work board and the shared money summary (Leads = Lead +
  // Qualified) file them the same way, so every screen agrees.
  return { stage: 'QUALIFIED', next: { kind: 'createQuote' }, quote }
}

/**
 * Every opportunity for one customer: their deals with linked documents, then
 * quote → job → invoice chains that belong to no deal. Open ones first,
 * newest first.
 */
export function buildOpportunities(input: { deals: ApiDeal[] } & Docs): Opportunity[] {
  const { deals, quotes, jobs, invoices, docsHidden } = input
  const usedQuotes = new Set<string>()
  const usedJobs = new Set<string>()
  const usedInvoices = new Set<string>()
  const out: Opportunity[] = []

  for (const deal of deals) {
    const dq = quotes.filter((q) => q.dealId === deal.id)
    const qIds = new Set(dq.map((q) => q.id))
    // Crew get no quotes: their jobs carry the deal id instead.
    const dj = jobs.filter((j) => (!!j.quoteId && qIds.has(j.quoteId)) || j.dealId === deal.id)
    const jIds = new Set(dj.map((j) => j.id))
    const di = invoices.filter(
      (i) => i.dealId === deal.id || (!!i.jobId && jIds.has(i.jobId)) || (!!i.quoteId && qIds.has(i.quoteId)),
    )
    dq.forEach((q) => usedQuotes.add(q.id))
    dj.forEach((j) => usedJobs.add(j.id))
    di.forEach((i) => usedInvoices.add(i.id))

    const d = deriveOpportunity(deal, { quotes: dq, jobs: dj, invoices: di, docsHidden })
    const stamps = [deal.updatedAt, ...dq.map((x) => x.updatedAt), ...dj.map((x) => x.updatedAt), ...di.map((x) => x.updatedAt)]
    out.push({
      id: `deal:${deal.id}`,
      deal,
      ...d,
      name: deal.name,
      value: d.invoice?.total ?? d.quote?.total ?? deal.value ?? 0,
      closed: d.stage === 'PAID' || d.stage === 'LOST',
      updatedAt: stamps.reduce((a, b) => (b > a ? b : a), deal.updatedAt),
    })
  }

  const chains = zipWorkItems(
    quotes.filter((q) => !usedQuotes.has(q.id)),
    jobs.filter((j) => !usedJobs.has(j.id)),
    invoices.filter((i) => !usedInvoices.has(i.id)),
  )
  for (const item of chains) {
    const d = deriveOpportunity(undefined, {
      quotes: item.quote ? [item.quote] : [],
      jobs: item.job ? [item.job] : [],
      invoices: item.invoice ? [item.invoice] : [],
      docsHidden,
    })
    out.push({
      id: opportunityRecordId(item),
      ...d,
      name:
        item.quote?.serviceCategory?.name ??
        item.job?.address ??
        item.quote?.quoteNumber ??
        item.invoice?.invoiceNumber ??
        'Work',
      value: item.total,
      closed: d.stage === 'PAID' || d.stage === 'LOST',
      updatedAt: item.updatedAt,
    })
  }

  return out.sort((a, b) => {
    if (a.closed !== b.closed) return a.closed ? 1 : -1
    return new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()
  })
}

/** One pastel per phase (lib/phaseTone): sales lavender, quote peach, production sky, billing butter, paid mint. */
export const OPPORTUNITY_STAGE_TONE: Record<OpportunityStage, 'lavender' | 'peach' | 'sky' | 'butter' | 'mint' | 'neutral'> = {
  LEAD: 'lavender',
  QUALIFIED: 'lavender',
  QUOTED: 'peach',
  SCHEDULED: 'sky',
  IN_PROGRESS: 'sky',
  INVOICED: 'butter',
  PAID: 'mint',
  LOST: 'neutral',
}

/** Button label for a next step — says what happens. */
export function nextStepLabel(next: OpportunityNextStep): string {
  switch (next.kind) {
    case 'createQuote': return 'Create quote'
    case 'openQuote': return 'Open quote'
    case 'createJob': return 'Create job'
    case 'openJob': return 'Open job'
    case 'createInvoice': return 'Create invoice'
    case 'openInvoice': return 'Open invoice'
  }
}

/**
 * "Lead since" until the contact's first payment, then "Customer since"
 * (the date of that first payment). A contact with no work at all was never
 * a lead: "Added" (shard 1: a new Customer read "Lead since").
 */
export function relationshipSince(
  contactCreatedAt: string,
  invoices: ApiInvoice[],
  opts: { hasWork?: boolean } = {},
): { label: string; date: string } {
  const paid = invoices
    .filter((i) => i.status === 'PAID' || (i.amountPaid ?? 0) > 0)
    .map((i) => i.paidAt ?? i.updatedAt)
    .filter(Boolean)
    .sort()
  if (paid.length > 0) return { label: 'Customer since', date: paid[0] }
  return { label: opts.hasWork === false ? 'Added' : 'Lead since', date: contactCreatedAt }
}

export interface OpportunitySideRow {
  /** Work record id to open. */
  id: string
  tone: (typeof OPPORTUNITY_STAGE_TONE)[OpportunityStage] | 'blush'
  label: string
  sub: string
  value: number
  /** Work with no quote behind it (a job booked straight away): show "No quote", not "$0.00". */
  noQuote: boolean
}

/**
 * The record's side panel ("Open work and invoices"): the open opportunities,
 * with the same stage the Opportunities tab shows. It used to read the raw
 * deal stage, so a deal whose invoice was out said "Qualified" beside a card
 * saying "Invoiced". Invoiced rows show the balance still owed.
 */
export function openOpportunityRows(opps: Opportunity[], today: Date = new Date()): OpportunitySideRow[] {
  const day = new Date(today)
  day.setHours(0, 0, 0, 0)
  return opps
    .filter((o) => !o.closed)
    .map((o) => {
      const inv = o.stage === 'INVOICED' ? o.invoice : undefined
      const late = !!inv && inv.status !== 'DRAFT' && (inv.status === 'OVERDUE' || (!!inv.dueDate && new Date(inv.dueDate) < day))
      const stage = late ? 'Overdue' : [OPPORTUNITY_STAGE_LABELS[o.stage], o.detail].filter(Boolean).join(' · ')
      const doc = inv?.invoiceNumber ?? (o.stage === 'QUOTED' ? o.quote?.quoteNumber : undefined)
      return {
        id: opportunityRecordId(o),
        tone: late ? 'blush' : OPPORTUNITY_STAGE_TONE[o.stage],
        label: o.name,
        sub: [stage, doc].filter(Boolean).join(' · '),
        value: inv ? inv.total - (inv.amountPaid ?? 0) : o.value,
        noQuote: !inv && !o.quote && !!o.job,
      }
    })
}
